import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { environment } from '@/environments/environment';

export interface LoadManagementDemoDialogData {
  title?: string;
  message: string;
  severity?: 'success' | 'info' | 'warning' | 'error';
  code?: string;
  meta?: Record<string, unknown> | null;
  /** When set, enables the Acknowledge button with the given event detail ID */
  eventId?: string;
  /** Account ID for the acknowledge API call */
  accountId?: string;
}

@Component({
  selector: 'app-load-management-demo-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <div class="modal-shell">
      <header class="modal-shell__header modal-shell__header--icon">
        <mat-icon class="modal-shell__icon" [ngClass]="data.severity ?? 'info'">bolt</mat-icon>
        <h1 class="modal-shell__title">{{ data.title || 'Load Management Update' }}</h1>
      </header>

      <mat-dialog-content class="modal-shell__content">
        <section class="modal-shell__card">
          <p class="modal-shell__body">{{ data.message }}</p>
          <ng-container *ngIf="data.meta as meta">
            <pre class="modal-shell__meta">{{ meta | json }}</pre>
          </ng-container>
          <p class="modal-shell__hint">
            Demo modal: choose a workflow path to simulate buyer or seller actions.
          </p>
          <p
            *ngIf="acknowledgeMessage"
            class="modal-shell__hint"
            style="color: #e65100; font-weight: 500; margin-top: 8px;"
          >
            {{ acknowledgeMessage }}
          </p>
        </section>
      </mat-dialog-content>

      <mat-dialog-actions class="modal-shell__actions" align="end">
        <button mat-button class="modal-shell__button" (click)="close('later')">
          Remind me later
        </button>
        <ng-container *ngIf="data.eventId && data.accountId">
          <button
            mat-flat-button
            color="warn"
            class="modal-shell__button modal-shell__button--acknowledge"
            (click)="acknowledge()"
            [disabled]="acknowledging"
          >
            <mat-icon>check_circle</mat-icon>
            {{ acknowledging ? 'Acknowledging...' : 'Acknowledge' }}
          </button>
        </ng-container>
        <button
          mat-stroked-button
          color="primary"
          class="modal-shell__button modal-shell__button--secondary"
          (click)="close('buy')"
        >
          <mat-icon>shopping_cart</mat-icon>
          Go to Buy Flow
        </button>
        <button
          mat-flat-button
          color="accent"
          class="modal-shell__button modal-shell__button--primary"
          (click)="close('sell')"
        >
          <mat-icon>bolt</mat-icon>
          Go to Sell Flow
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .modal-shell__button mat-icon {
        margin-right: 0.25rem;
      }
    `,
  ],
})
export class LoadManagementDemoDialogComponent {
  private http = inject(HttpClient);
  acknowledging = false;

  constructor(
    private dialogRef: MatDialogRef<LoadManagementDemoDialogComponent, string>,
    @Inject(MAT_DIALOG_DATA)
    readonly data: LoadManagementDemoDialogData,
  ) {}

  close(result: string) {
    this.dialogRef.close(result);
  }

  acknowledgeMessage = '';

  acknowledge() {
    if (!this.data.eventId || !this.data.accountId) return;
    this.acknowledging = true;

    this.http
      .post<{
        acknowledged?: boolean;
        alreadyAcknowledged?: boolean;
        acknowledgedByName?: string;
      }>(
        `${environment.apiUrl}/v1/load-management/${this.data.accountId}/events/${this.data.eventId}/acknowledge`,
        {},
      )
      .subscribe({
        next: result => {
          this.acknowledging = false;
          if (result?.alreadyAcknowledged) {
            this.acknowledgeMessage = `Already acknowledged by ${result.acknowledgedByName || 'another user'}`;
          } else {
            this.dialogRef.close('acknowledged');
          }
        },
        error: () => {
          this.acknowledging = false;
        },
      });
  }
}
